import { ArrowRight, Check, Database } from 'lucide-react';
import { useEffect } from 'react';
import { InView } from 'react-intersection-observer';
import useScrollBehavior from '../landing/useScrollBehavior';
import Footer from './components/footer';
import Header from './components/header';
import InfoCards from './components/infoCards';
import ProjectForm from './components/projectForm';
import './inform3d.css';
import { EXTRACTION_APPROACHES, QUALITY_CHECKS, SERVICES } from './inform3d.content';

const INTERSECTION_THRESHOLD = 0.25;

export default function Inform3D() {
    const { scrollToElement, scrollToTop } = useScrollBehavior();

    useEffect(() => {
        scrollToTop('auto');
    }, [scrollToTop]);

    return (
        <div id="app">
            <Header />
            <main id="main-content">
                <section id="project-form" className="inform__hero">
                    <div className="inform__hero-split">
                        <div className="inform__hero-content">
                            <h1 className="inform__hero-title">
                                Complex Documents,
                                <br />
                                <span className="inform__highlight">Intelligently Structured</span>
                            </h1>

                            <p className="inform__hero-description">
                                Transform unstructured documents into accurate, structured, AI-compatible data -
                                combining automated extraction with experienced human verification.
                            </p>

                            <div className="inform__hero-stats">
                                <div className="inform__hero-stat">
                                    <strong>4</strong>
                                    <span>extraction approaches</span>
                                </div>
                                <div className="inform__hero-stat">
                                    <strong>1-15</strong>
                                    <span>specialists per project</span>
                                </div>
                                <div className="inform__hero-stat">
                                    <strong>multiple</strong>
                                    <span>layers of QA</span>
                                </div>
                            </div>
                        </div>

                        <ProjectForm />
                    </div>
                </section>

                <InView triggerOnce threshold={INTERSECTION_THRESHOLD}>
                    {({ inView, ref }) => (
                        <section
                            id="services"
                            ref={ref}
                            className={`inform__services fadeInUp ${inView ? 'fadeInUp--visible' : 'fadeInUp--hidden'}`}
                        >
                            <div className="sp-container-md">
                                <div className="sp-section-header">
                                    <h2>What We Take On</h2>
                                    <div className="sp-divider"></div>
                                    <p className="sp-section-description">
                                        Inform3D supports organizations and individuals that need to digitize documents,
                                        extract and reformat information, build databases or automate repetitive
                                        document-processing tasks.
                                    </p>
                                </div>

                                <InfoCards cards={SERVICES} />
                            </div>
                        </section>
                    )}
                </InView>

                <InView triggerOnce threshold={INTERSECTION_THRESHOLD}>
                    {({ inView, ref }) => (
                        <section
                            id="approaches"
                            ref={ref}
                            className={`inform__approaches fadeInUp ${
                                inView ? 'fadeInUp--visible' : 'fadeInUp--hidden'
                            }`}
                        >
                            <div className="sp-container-md">
                                <div className="sp-section-header">
                                    <h2>Which Approach Do You Need? </h2>
                                    <div className="sp-divider"></div>
                                    <p className="sp-section-description">
                                        We transform unstructured data into accurate, structured datasets, using one or
                                        a combination of the following approaches.
                                    </p>
                                </div>

                                <InfoCards cards={EXTRACTION_APPROACHES} />

                                <div className="inform__approaches-footer">
                                    <p>
                                        Select one or more approaches, or simply contact us, we will advise based on
                                        your data and personal needs.
                                    </p>
                                </div>
                            </div>
                        </section>
                    )}
                </InView>

                <InView triggerOnce threshold={INTERSECTION_THRESHOLD}>
                    {({ inView, ref }) => (
                        <section
                            id="quality"
                            ref={ref}
                            className={`inform__qa fadeInUp ${inView ? 'fadeInUp--visible' : 'fadeInUp--hidden'}`}
                        >
                            <div className="sp-container-lg">
                                <div className="sp-section-header">
                                    <h2>Whichever Approach or Need You Have, QA Comes With It</h2>
                                    <div className="sp-divider"></div>
                                    <p className="sp-section-description">
                                        Your project will be supported by rigorous quality assurance, scaled to what
                                        your data demands.
                                    </p>
                                </div>

                                <div className="inform__qa-list">
                                    {QUALITY_CHECKS.map((check) => (
                                        <div key={check.title} className="inform__qa-item">
                                            <span className="inform__qa-check">
                                                <Check className="sp-icon-md" />
                                            </span>
                                            <div>
                                                <h3>{check.title}</h3>
                                                <p>{check.description}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </section>
                    )}
                </InView>

                <section className="inform__scale">
                    <div className="sp-container-md">
                        <div className="inform__scale-inner">
                            <div className="inform__scale-number">1-15</div>
                            <div className="inform__scale-text">
                                <h3>Data specialists per project</h3>
                                <p>
                                    A single specialist for a focused task, or a team of up to fifteen for large-scale
                                    digitization. We size the team to your deadline and demands.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                <InView triggerOnce threshold={INTERSECTION_THRESHOLD}>
                    {({ inView, ref }) => (
                        <section
                            id="about"
                            ref={ref}
                            className={`inform__why fadeInUp ${inView ? 'fadeInUp--visible' : 'fadeInUp--hidden'}`}
                        >
                            <div className="sp-container-md">
                                <div className="sp-section-header">
                                    <h2>Why Inform3D</h2>
                                    <div className="sp-divider"></div>
                                </div>

                                <div className="sp-card sp-card--primary">
                                    <div className="sp-card__header">
                                        <Database className="sp-icon-md" />
                                        Established by Running a Database of Our Own
                                    </div>
                                    <div className="sp-card__content">
                                        <p>
                                            Inform3D is an established data entry agency, bringing years of experience
                                            in data extraction, document processing, and quality assurance gained while
                                            building a comprehensive financial database - 3D StockPicker.
                                        </p>
                                        <div className="sp-callout">
                                            <p>
                                                Through our large-scale project, we developed efficient workflows and
                                                reliable tools that we now leverage and adapt to meet each of your
                                                unique requirements.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </section>
                    )}
                </InView>

                <section className="inform__cta">
                    <div className="sp-container-sm">
                        <h2>Still weighing it up?</h2>
                        <p>
                            Send us a sample document. We will tell you which approach fits and discuss the best way to
                            process your information accurately and consistently — before you commit to anything.
                        </p>
                        <button
                            onClick={() => scrollToElement('project-form')}
                            className="sp-btn sp-btn--primary sp-btn--lg"
                        >
                            Start Your Project
                            <ArrowRight className="sp-icon-md" />
                        </button>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
